import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { cloneDeep } from 'lodash'
import { isAxiosError } from 'axios'
import { logger } from 'shared/lib'
import { RequestCancelAxios, RequestLoadData } from 'shared/api/lib'
import { TableStore } from 'shared/ui/TableNew'
import { layoutStore } from 'shared/layout'
import { CallHistory } from 'entities/CallHistory/model/CallHistory'
import { ConversationsApi, type IReadMessage } from 'entities/Conversation'
import { UsersApi } from 'entities/Users'
import {
  type IParamsGetCallHistory,
  type IParamsGetCallHistoryStatistic,
  type IResponseCallHistory,
  type IResponseCallHistoryItem,
} from 'entities/CallHistory/api/types'
import { RowCallHistory } from 'entities/CallHistory/model/Table/RowCallHistory'
import { tableLimitStore } from 'entities/TableLimit'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { CallHistoryApi } from 'entities/CallHistory/api/callHistory'
import type { IResponseEventInboxCalling } from 'entities/Inbox/api/types'
import { type CallHistoryFiltersStore } from 'pages/conversations/pages/calls/ui/CallHistoryFilters'
import { ExportCallHistoryModalStore } from 'pages/conversations/pages/calls/ui/ExportCallHistoryModal'
import type { FiltersListKey } from 'pages/conversations/pages/calls/ui/CallHistoryDropdownFilter'

const defaultSortBy = 'created_at'

export class CallHistoryTableStore {
  readonly exportCallHistoryModalStore: ExportCallHistoryModalStore

  private _itemsRowsMap: Map<number, RowCallHistory> = new Map()

  private _cancelRequestGetUsersUiSettings = new RequestCancelAxios()
  readonly tableStore: TableStore<RowCallHistory>
  private _group: FiltersListKey = 'all'

  private _disposeReactionParamsWithoutPage: IReactionDisposer | null = null
  private _disposeReactionParamsGetItems: IReactionDisposer | null = null
  private _disposeLayoutResize: IReactionDisposer | null = null

  private _search: string | null = null
  private _loading = true
  private _loadingMore = false
  private _loadingSearch = false
  private _hasRetriedAtMinLimit = false
  private _isRetrying = false
  private _isInfinityList = false

  constructor(
    private _entities: IEntitiesStore,
    private _callHistoryFiltersStore: CallHistoryFiltersStore
  ) {
    this.tableStore = new TableStore<RowCallHistory>({
      key: 'CallHistoryTable',
      page: 1,
      limit: 10,
      getLimit: () => tableLimitStore.getLimit('call-history-table'),
      setLimit: (value) => {
        tableLimitStore.setLimit('call-history-table', value)
      },
      sortOrder: 'desc',
      sortBy: defaultSortBy,
      useColumnsCache: true,
      getColumns: this.getSelectedColumns,
      saveColumns: this.updateSelectedColumns,
      onChangeColumns: (ids, store) => {
        const hasLastColumn = !(
          ids?.includes('created_at') ||
          ids?.includes('user') ||
          ids?.includes('inbox')
        )

        store.updateColumn('audio', {
          minWidth: hasLastColumn ? 260 : 94,
        })
      },
      usePaginationPage: true,
    })
    this.exportCallHistoryModalStore = new ExportCallHistoryModalStore()

    makeAutoObservable(this)
  }

  setIsInfinityList = (value: boolean) => {
    this._isInfinityList = value
  }

  getSortingParams = () => {
    return {
      field: this.tableStore.sortBy,
      order: this.tableStore.sortOrder,
    }
  }

  markAsRead = async (item: CallHistory) => {
    try {
      await ConversationsApi.updateMessagesByIdRead(item.conversation.id, {
        ids: [item.message.id],
      }).then(() => {
        const clonedItem = cloneDeep(item)
        clonedItem.message.unread = false
        this.updateItem(clonedItem)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  updateSelectedColumns = async (ids: string[]) => {
    const { data } = await UsersApi.updateUsersUiSettings({
      source_type: 'call-history-table-columns',
      items: ids,
    })

    return data?.data?.items ?? []
  }

  getSelectedColumns = async (): Promise<string[]> => {
    try {
      this._cancelRequestGetUsersUiSettings.init()

      const { data } = await UsersApi.getUsersUiSettings('call-history-table-columns', {
        cancelToken: this._cancelRequestGetUsersUiSettings.token,
      })

      return data.data?.items ?? []
    } catch (e) {
      logger.error(e)

      return []
    }
  }

  dispose = () => {
    this._requestLoadData.dispose()
    this.exportCallHistoryModalStore.dispose()
    this._cancelRequestGetUsersUiSettings.reset()
  }

  handleLoadMore = () => {
    if (this._loadingMore) return

    this._loadingMore = true
    this.tableStore.setPage(this.tableStore.page + 1)
  }

  initReactions = () => {
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
    this.reactionLayoutResize()
  }

  disposeReactions = () => {
    this._disposeReactionParamsWithoutPage?.()
    this._disposeReactionParamsGetItems?.()
    this._disposeLayoutResize?.()
  }

  reactionLayoutResize = () => {
    this._disposeLayoutResize?.()
    this._disposeLayoutResize = reaction(
      () => layoutStore.isViewXXsGlobal,
      (value) => {
        this.tableStore.columnsStore.updateColumn('contact', {
          minWidth: value ? 180 : 192,
        })
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionParamsWithoutPage = () => {
    this._disposeReactionParamsWithoutPage?.()
    this._disposeReactionParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._loading = true
        this.tableStore.setLoading(true)
        this.tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeReactionParamsGetItems?.()
    this._disposeReactionParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => {
        this._loading = true
        this.tableStore.setLoading(true)
        this._requestLoadData.debounceLoadData()
      }
    )
  }

  setData = ({ data, meta }: IResponseCallHistory) => {
    this.setItems(data)
    this.tableStore.setTotal(data.length)
    this.tableStore.setHasMore(meta.has_more)
  }

  setItems = (items: IResponseCallHistoryItem[]) => {
    items.forEach((item) => this.setItem(item))
  }

  setItem = (response: IResponseCallHistoryItem) => {
    const item = new CallHistory(response, this._entities)

    this._itemsRowsMap.set(item.message.id, new RowCallHistory(item))
  }

  updateItem = (item: CallHistory) => {
    this._itemsRowsMap.set(item.message.id, new RowCallHistory(item))
  }

  getItem = (id: number) => {
    return this._itemsRowsMap.get(id)
  }

  clear = () => {
    this._itemsRowsMap.clear()
    this._loading = true
    this._loadingMore = false
    this._search = null
    this.tableStore.setLoading(true)
    this.tableStore.setPage(1)
    this.tableStore.setHasMore(null)
  }

  openExportModal = () => {
    this.exportCallHistoryModalStore.onOpenExport(this.paramsWithoutPage)
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      if (this._search && this._entities.organizationStore.isHipaaCompliant) {
        this._entities.organizationStore.handleTrackOrganizationActivity({
          event: 'phi.contact.searched',
          data: {
            search_query: this._search,
          },
        })
      }

      this.tableStore.setLoading(true)
      this._loading = true

      try {
        const { data } = await CallHistoryApi.getCallHistory(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })

        if (!this._isInfinityList || (this.paramsGetItems.page && this.paramsGetItems.page === 1)) {
          runInAction(() => {
            this._itemsRowsMap.clear()
          })
        }

        this.setData(data)

        runInAction(() => {
          this._hasRetriedAtMinLimit = false
          this._loading = false
          this._loadingSearch = false
          this.tableStore.setLoading(false)
        })
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: call-history-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this.tableStore.limit
          const newLimit = tableLimitStore.reduceLimit('call-history-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
              this._isRetrying = true
              this.tableStore.setLimit(newLimit)
            })
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
              this._isRetrying = true
            })
            this._requestLoadData.debounceLoadData()
          }
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        if (!this._isRetrying) {
          this._loading = false
          this._loadingMore = false
          this.tableStore.setLoading(false)
        }
        this._isRetrying = false
      })
    },
  })

  loadCallHistory = async () => {
    return this._requestLoadData.loadData()
  }

  handleSearch = (key: string) => {
    this._search = key
    this._loadingSearch = true
  }

  setGroup = (key: string) => {
    this._group = key
  }

  updateInboxCalling = (data: IResponseEventInboxCalling) => {
    this._itemsRowsMap.forEach((item) => {
      if (item.inbox.id === data.team_id) {
        item.origin.updateInboxCalling(data.number)
      }
    })
  }

  markAsReadByConversation = (conversationId: number, inboxId: number) => {
    this._itemsRowsMap.forEach((item) => {
      if (item.origin.conversation.id === conversationId && item.inbox.id === inboxId) {
        item.origin.markAsReadSync()
      }
    })
  }

  updateCallHistoryByMessageReceivedEvent = (data: IResponseCallHistoryItem) => {
    const { call, message } = data
    const { id, unread } = message

    if (this.getItem(id)) {
      this.setItem(data)
    } else {
      if (unread) {
        this._callHistoryFiltersStore.increaseCount(call)
      }

      this.setAdditionItem(data)
    }
  }

  updateCallHistoryByVoicemailEvent = (data: IResponseCallHistoryItem) => {
    const { call, message } = data
    const { id, unread } = message
    const existingItem = this.getItem(id)

    if (existingItem) {
      if (existingItem.origin.message.unread && existingItem.call.type) {
        const item: IReadMessage = {
          id: existingItem.origin.message.id,
          type: 'call',
          direction: existingItem.call.type,
          status: existingItem.call.outcome,
          source: existingItem.call.source,
        }

        this._callHistoryFiltersStore.decreaseCount([item])
      }

      this.setItem(data)
    } else {
      this.setAdditionItem(data)
    }

    if (unread) {
      this._callHistoryFiltersStore.increaseCount(call)
    }
  }

  setAdditionItem = (item: IResponseCallHistoryItem) => {
    if (!this._callHistoryFiltersStore.hasCalls) {
      const params: IParamsGetCallHistoryStatistic = {
        team_id: this._entities.inboxesStore.currentInboxTypeId,
        team_type: this._entities.inboxesStore.currentInboxType || 3,
        group: this._group === 'all' ? null : this._group,
      }

      this._callHistoryFiltersStore.loadData(params)
    }

    if (this._callHistoryFiltersStore.isActiveAllCalls) {
      this.setItemToBegin(item)

      return
    }

    const { type, outcome, source } = item.call

    const activeFilter = this._callHistoryFiltersStore.activeFilter
    const matchesFilter = activeFilter?.source
      ? activeFilter.type === type && activeFilter.source === source
      : activeFilter?.type === type && activeFilter.outcome === outcome

    if (matchesFilter) {
      this.setItemToBegin(item)
    }
  }

  setItemToBegin = (item: IResponseCallHistoryItem) => {
    const currentItems = cloneDeep(this.items)

    this._itemsRowsMap.clear()
    this.setItem(item)
    this.setItems(currentItems)
  }

  get paramsWithoutPage(): Omit<IParamsGetCallHistory, 'page'> {
    return {
      search: this._search || null,
      limit: this.tableStore.limit,
      team_id: this._entities.inboxesStore.currentInboxTypeId,
      team_type: this._entities.inboxesStore.currentInboxType || 3,
      group: this._group === 'all' ? null : this._group,
      filtersList: this._callHistoryFiltersStore.filtersList,
      sorting: [this.getSortingParams()],
    }
  }

  get paramsGetItems(): IParamsGetCallHistory {
    return {
      page: this.tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get itemsRows() {
    return Array.from(this._itemsRowsMap.values())
  }

  get items() {
    return this.itemsRows.map((item) => item.origin)
  }

  get isLoading() {
    return this._loadingSearch || this._loading
  }

  get isEmptyState() {
    return !this._loading && !this._itemsRowsMap.size && !this._search
  }

  get isHasMore() {
    return this.tableStore.hasMore
  }

  get loading() {
    return this._loading
  }

  get loadingMore() {
    return this._loadingMore
  }

  get loadingSearch() {
    return this._loadingSearch
  }

  get isEmptySearchResults() {
    return (
      (Boolean(this._search) ||
        Boolean(this._callHistoryFiltersStore.filtersList.length) ||
        this._group !== 'open') &&
      !this.items.length &&
      !this._loadingSearch
    )
  }

  get search() {
    return this._search
  }
}
